import { useEffect, useState, useMemo, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Beer, GlassWater, Coffee, Wine, Martini, Flame, UtensilsCrossed, Cigarette, CircleDot, CupSoda, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_GROUPS = [
  { key: "fassbier", label: "Fassbier", icon: Beer, color: "#f59e0b", categories: ["Fassbier"] },
  { key: "flaschenbier", label: "Flaschenbier", icon: Beer, color: "#d97706", categories: ["Flaschenbier"] },
  { key: "af", label: "Alkoholfrei", icon: GlassWater, color: "#06b6d4", categories: ["AF", "AF Fl"] },
  { key: "heiss", label: "Heißgetränke", icon: Coffee, color: "#a16207", categories: ["Heißgetränke"] },
  { key: "wein", label: "Wein", icon: Wine, color: "#dc2626", categories: ["Wein offen", "Wein FL"] },
  { key: "cocktails", label: "Cocktails", icon: Martini, color: "#e879f9", categories: ["Cocktails"] },
  { key: "mix", label: "Longdrinks & Mix", icon: CupSoda, color: "#22d3ee", categories: ["Mix", "Mix Fl"] },
  { key: "spirituosen", label: "Spirituosen", icon: Flame, color: "#ef4444", categories: ["Spirituosen", "Spirituosen FL"] },
  { key: "speisen", label: "Speisen & Snacks", icon: UtensilsCrossed, color: "#22c55e", categories: ["Pizza", "Baguettes", "Snack", "Speisen", "Knabberli süss", "Knabberli sauer"] },
  { key: "tabak", label: "Tabak", icon: Cigarette, color: "#78716c", categories: ["Tabak"] },
  { key: "billard", label: "Billard & Spiele", icon: CircleDot, color: "#10b981", categories: ["Billard"] },
];

// Allergen mapping for food items
function getAllergens(name, category) {
  const n = name.toLowerCase();
  if (category === "Pizza" || category === "Baguettes") {
    if (n.includes("donut")) return ["A", "G", "L", "M"];
    if (n.includes("thun")) return ["A", "D", "G"];
    return ["A", "G"];
  }
  if (category === "Snack") return ["A", "G"];
  return null;
}

// Extract base product name by removing trailing size info (0,2 / 0,3l / 2cl / 4cl / 1l etc.)
function getBaseName(name) {
  return name
    .replace(/\s+\d+[,/.]\d*\s*(l|cl|ml|lt|ltr)?\s*$/i, '')
    .replace(/\s+\d+\s*(l|cl|ml|lt|ltr)\s*$/i, '')
    .replace(/\s+\d+\/\d+\s*(l|cl|ml)?\s*$/i, '')
    .trim();
}

function smartSort(items, groupKey) {
  // For Fassbier: push kegs (20L, 50L) to the bottom
  if (groupKey === "fassbier") {
    const isKeg = (name) => /\d+\s*L$/i.test(name) || /\d+L$/i.test(name);
    const regular = items.filter(i => !isKeg(i.name));
    const kegs = items.filter(i => isKeg(i.name));
    // Sort kegs by volume (smaller first)
    kegs.sort((a, b) => {
      const volA = parseInt(a.name.match(/(\d+)\s*L$/i)?.[1] || '0');
      const volB = parseInt(b.name.match(/(\d+)\s*L$/i)?.[1] || '0');
      return volA - volB;
    });
    return [...sortByBaseName(regular), ...kegs];
  }
  return sortByBaseName(items);
}

function sortByBaseName(items) {
  // Group by base name, sort groups alphabetically, within group sort by price ascending
  const groups = {};
  items.forEach(item => {
    const base = getBaseName(item.name).toLowerCase();
    if (!groups[base]) groups[base] = [];
    groups[base].push(item);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'de'));
  const result = [];
  sortedKeys.forEach(key => {
    groups[key].sort((a, b) => a.price - b.price);
    result.push(...groups[key]);
  });
  return result;
}

export default function PricelistPage() {
  usePageTitle("Getränkekarte & Preise");
  const [items, setItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/pricelist`).then(r => setItems(r.data)).catch(() => {});
    axios.get(`${API}/special-offers`).then(r => setOffers(r.data)).catch(() => {});
  }, []);

  const searchLower = search.toLowerCase().trim();

  const getGroupItems = useCallback((group) => {
    let groupItems = items.filter(item => group.categories.includes(item.category));
    if (searchLower) {
      groupItems = groupItems.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        (item.description && item.description.toLowerCase().includes(searchLower))
      );
    }
    return smartSort(groupItems, group.key);
  }, [items, searchLower]);

  const groupsWithItems = useMemo(() => {
    return CATEGORY_GROUPS.map(g => ({ ...g, items: getGroupItems(g) })).filter(g => g.items.length > 0);
  }, [getGroupItems]);

  const totalResults = groupsWithItems.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="pricelist-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
          Getränke<span style={{ color: '#fbbf24' }}>karte</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-6">Getränke, Speisen & Preise</p>

        {/* Search */}
        <div className="relative mb-8" data-testid="pricelist-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Suche nach Getränk, Speise..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-card border-white/10 rounded-none h-11 text-sm"
            data-testid="pricelist-search-input"
          />
          {searchLower && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {totalResults} Ergebnis{totalResults !== 1 ? 'se' : ''}
            </span>
          )}
        </div>

        {/* Special Offers */}
        {offers.length > 0 && !searchLower && (
          <div className="mb-10 p-6 bg-card border-2 border-primary/30" data-testid="special-offers">
            <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>
              Aktuelle Angebote
            </h2>
            <div className="space-y-3">
              {offers.map(offer => (
                <div key={offer.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="font-semibold text-sm">{offer.title}</p>
                    <p className="text-xs text-muted-foreground">{offer.description}</p>
                  </div>
                  {offer.valid_until && (
                    <Badge variant="outline" className="text-xs">bis {new Date(offer.valid_until).toLocaleDateString('de-AT')}</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No results */}
        {searchLower && groupsWithItems.length === 0 && (
          <div className="text-center py-16 text-muted-foreground" data-testid="no-results">
            <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Keine Ergebnisse für „{search}&quot;</p>
          </div>
        )}

        {/* Menu */}
        <Accordion type="single" collapsible className="space-y-2">
          {groupsWithItems.map(group => {
            const Icon = group.icon;
            const hasSubcats = group.categories.length > 1;
            const subcatItems = hasSubcats
              ? group.categories.map(cat => ({
                  cat,
                  items: smartSort(group.items.filter(i => i.category === cat), group.key)
                })).filter(s => s.items.length > 0)
              : null;

            return (
              <AccordionItem key={group.key} value={group.key} className="bg-card border border-white/5 px-0"
                data-testid={`menu-group-${group.key}`}>
                <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-white/[0.02] [&[data-state=open]]:border-b [&[data-state=open]]:border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-md" style={{ backgroundColor: `${group.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: group.color }} />
                    </div>
                    <span className="text-base font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>{group.label}</span>
                    <Badge variant="secondary" className="text-xs ml-1">{group.items.length}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  {hasSubcats && subcatItems ? (
                    subcatItems.map(subcat => (
                      <div key={subcat.cat}>
                        <div className="px-5 py-2 bg-white/[0.02] border-b border-white/5">
                          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{subcat.cat}</span>
                        </div>
                        <div className="divide-y divide-white/5">
                          {subcat.items.map(item => (
                            <MenuItem key={item.id} item={item} groupColor={group.color} searchTerm={searchLower} />
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="divide-y divide-white/5">
                      {group.items.map(item => (
                        <MenuItem key={item.id} item={item} groupColor={group.color} searchTerm={searchLower} />
                      ))}
                    </div>
                  )}
                  {group.key === "speisen" && <AllergenNotice />}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <p className="mt-8 text-xs text-muted-foreground text-center">
          Alle Preise in Euro. Änderungen, Druckfehler und Irrtümer vorbehalten. Es gelten die Preise laut aktueller Getränkekarte im Lokal.
        </p>
      </div>
    </div>
  );
}

function MenuItem({ item, groupColor, searchTerm }) {
  const highlightMatch = (text) => {
    if (!searchTerm || !text) return text;
    const idx = text.toLowerCase().indexOf(searchTerm);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-primary/30 text-primary font-semibold">{text.slice(idx, idx + searchTerm.length)}</span>
        {text.slice(idx + searchTerm.length)}
      </>
    );
  };

  const allergens = getAllergens(item.name, item.category);

  return (
    <div className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
      data-testid={`menu-item-${item.id}`}>
      <div className="flex-1 min-w-0 mr-4">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{highlightMatch(item.name)}</p>
          {allergens && (
            <span className="text-xs font-bold text-primary/80 whitespace-nowrap" data-testid={`allergen-${item.id}`}>
              {allergens.join(",")}
            </span>
          )}
        </div>
        {item.description && <p className="text-xs text-muted-foreground mt-0.5">{/^(Pizza|Baguette|Toast|Snack)/.test(item.category) ? item.description.replace(/\bPortion\b/gi, 'Stück') : item.description}</p>}
      </div>
      <span className="text-sm font-bold whitespace-nowrap tabular-nums" style={{ color: groupColor }}>
        {item.price.toFixed(2)} €
      </span>
    </div>
  );
}


function AllergenNotice() {
  return (
    <div className="border-t border-white/10 mt-2" data-testid="allergen-notice">
      <div className="px-5 py-3 border-b border-white/5">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Allergene</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 px-5 py-4 text-xs">
        <div><span className="font-bold text-primary">A</span> <span className="text-muted-foreground">Glutenhaltiges Getreide und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">B</span> <span className="text-muted-foreground">Krebstiere und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">C</span> <span className="text-muted-foreground">Eier von Geflügel und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">D</span> <span className="text-muted-foreground">Fisch und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">E</span> <span className="text-muted-foreground">Erdnüsse und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">F</span> <span className="text-muted-foreground">Soja und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">G</span> <span className="text-muted-foreground">Milch und Milcherzeugnisse (inkl. Laktose)</span></div>
        <div><span className="font-bold text-primary">H</span> <span className="text-muted-foreground">Nüsse und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">L</span> <span className="text-muted-foreground">Sellerie und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">M</span> <span className="text-muted-foreground">Senf und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">N</span> <span className="text-muted-foreground">Sesamsamen und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">O</span> <span className="text-muted-foreground">Schwefeldioxid und Sulfite</span></div>
        <div><span className="font-bold text-primary">P</span> <span className="text-muted-foreground">Lupinen und daraus gewonnene Erzeugnisse</span></div>
        <div><span className="font-bold text-primary">R</span> <span className="text-muted-foreground">Weichtiere (Schnecken, Muscheln, Tintenfische)</span></div>
      </div>
      <div className="px-5 py-3 border-t border-white/5 text-center">
        <p className="text-xs text-muted-foreground">Unsere Mitarbeiter informieren Sie gerne über allergene Zutaten in unseren Gerichten.</p>
      </div>
    </div>
  );
}
